// import { gameConfig } from '../gameConfig';

const DEBUG_MODE = true;

const debugMessage = (msg, enable) => enable && console.log(`[AutoSolver] ${msg}`);

export class AutoSolver {
  constructor(game, onMoveCallback, onMoveRecordCallback) {
    this.game = game;
    this.onMoveCallback = onMoveCallback;
    this.onMoveRecordCallback = onMoveRecordCallback;
    this.moveValues = new Map();
    this.shouldStop = false;
  }

  /**
   * 计算指定格子两侧指向该格的滑块数量
   * 指向是方向性的，可以隔着多个格子指向后面的格子
   */
  countSlidersPointingTo(x, y) {
    let count = 0;

    for (const slider of this.game.sliders) {
      const [, headX, headY, sDx, sDy, alive] = slider;
      if (!alive) continue;

      const sliderDir = `${sDx},${sDy}`;
      
      if (sliderDir === '1,0') {
        for (let checkX = x - 1; checkX >= 0; checkX--) {
          if (headX === checkX && headY === y) {
            count++;
            break;
          }
        }
      } else if (sliderDir === '-1,0') {
        for (let checkX = x + 1; checkX < this.game.m; checkX++) {
          if (headX === checkX && headY === y) {
            count++;
            break;
          }
        }
      } else if (sliderDir === '0,1') {
        for (let checkY = y - 1; checkY >= 0; checkY--) {
          if (headX === x && headY === checkY) {
            count++;
            break;
          }
        }
      } else if (sliderDir === '0,-1') {
        for (let checkY = y + 1; checkY < this.game.n; checkY++) {
          if (headX === x && headY === checkY) {
            count++;
            break;
          }
        }
      }
    }

    return count;
  }

  /**
   * 计算滑块的运动价值
   */
  calculateMoveValue(slider) {
    const [id, headX, headY, dx, dy, alive] = slider;
    
    if (!alive) {
      debugMessage(`滑块 ${id} 已死亡`, DEBUG_MODE);
      return -Infinity;
    }
    
    if (!this.game.canMoveSlider(slider)) {
      debugMessage(`滑块 ${id} 不可移动`, DEBUG_MODE);
      return -Infinity;
    }

    const cells = this.game.getSliderCells(slider);
    const tailCell = cells[cells.length - 1];
    const [tailX, tailY] = tailCell;

    const nextHeadX = headX + dx;
    const nextHeadY = headY + dy;

    const tailValue = this.countSlidersPointingTo(tailX, tailY);
    const headValue = this.countSlidersPointingTo(nextHeadX, nextHeadY);

    const moveValue = tailValue - headValue;
    debugMessage(`滑块 ${id}: 尾巴(${tailX},${tailY})价值=${tailValue}, 头部前方(${nextHeadX},${nextHeadY})价值=${headValue}, 运动价值=${moveValue}`, DEBUG_MODE);
    
    return moveValue;
  }

  /**
   * 计算所有滑块的运动价值
   */
  calculateAllMoveValues() {
    this.moveValues.clear();

    for (const slider of this.game.sliders) {
      const value = this.calculateMoveValue(slider);
      this.moveValues.set(slider[0], value);
      debugMessage(`滑块 ${slider[0]} 运动价值: ${value}`, DEBUG_MODE);
    }
  }

  /**
   * 获取运动价值最高的滑块
   */
  getBestSlider() {
    let maxValue = -Infinity;
    let bestSliders = [];

    for (const [sliderId, value] of this.moveValues) {
      if (value > maxValue) {
        maxValue = value;
        bestSliders = [sliderId];
      } else if (value === maxValue) {
        bestSliders.push(sliderId);
      }
    }

    debugMessage(`最大运动价值: ${maxValue}, 候选滑块:`, DEBUG_MODE);

    if (bestSliders.length === 0 || maxValue === -Infinity) {
      debugMessage('没有可移动的滑块', DEBUG_MODE);
      return null;
    }

    const randomIndex = Math.floor(Math.random() * bestSliders.length);
    const selectedSlider = bestSliders[randomIndex];
    debugMessage(`选择滑块: ${selectedSlider}`, DEBUG_MODE);
    return selectedSlider;
  }

  /**
   * 获取受影响的滑块（需要重新计算运动价值的滑块）
   * 当一个滑块移动时，会影响所有可能指向受影响位置或其目标位置的滑块
   */
  getAffectedSliders(movedSlider) {
    const [, oldHeadX, oldHeadY, dx, dy] = movedSlider;
    const affectedSliders = new Set();

    const cells = this.game.getSliderCells(movedSlider);
    const oldTailCell = cells[cells.length - 1];
    const [oldTailX, oldTailY] = oldTailCell;

    const newHeadX = oldHeadX + dx;
    const newHeadY = oldHeadY + dy;
    const newTailX = oldTailX + dx;
    const newTailY = oldTailY + dy;

    // Positions that have occupancy status changed due to the move
    const changedPositionsSet = new Set();
    [[oldHeadX, oldHeadY], [oldTailX, oldTailY], [newHeadX, newHeadY], [newTailX, newTailY]]
      .forEach(([x, y]) => changedPositionsSet.add(`${x},${y}`));

    // Check each slider to see if it's affected
    for (const slider of this.game.sliders) {
      const [id, headX, headY, sDx, sDy, alive] = slider;
      if (!alive || id === movedSlider[0]) continue; // Skip the moved slider itself and dead sliders

      // Get this slider's tail position
      const thisSliderCells = this.game.getSliderCells(slider);
      const thisTailCell = thisSliderCells[thisSliderCells.length - 1];
      const [thisTailX, thisTailY] = thisTailCell;

      // Next position where this slider would move to
      const nextThisHeadX = headX + sDx;
      const nextThisHeadY = headY + sDy;

      // A slider is affected if:
      // 1. Its head position is in a changed position
      // 2. Its tail position is in a changed position  
      // 3. Its next head position is in a changed position
      // 4. Any position that contributes to its value calculation is affected
      
      const checkPosKey = (x, y) => `${x},${y}`;
      
      if (changedPositionsSet.has(checkPosKey(headX, headY)) ||
          changedPositionsSet.has(checkPosKey(thisTailX, thisTailY)) ||
          changedPositionsSet.has(checkPosKey(nextThisHeadX, nextThisHeadY))) {
        affectedSliders.add(id);
        continue;
      }
      
      // Also affected if positions that are checked by countSlidersPointingTo are changed
      // This includes positions adjacent to this slider's tail and next head positions
      const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      
      // Check if any position adjacent to this slider's tail is changed
      // (these positions affect the tailValue calculation)
      for (const [dX, dY] of directions) {
        const adjX = thisTailX + dX;
        const adjY = thisTailY + dY;
        if (this.game.isValidPosition(adjX, adjY) && changedPositionsSet.has(checkPosKey(adjX, adjY))) {
          affectedSliders.add(id);
          break;
        }
      }
      
      // Check if any position adjacent to where this slider would move is changed
      // (these positions affect what can point to the next head position)
      if (!affectedSliders.has(id)) {
        for (const [dX, dY] of directions) {
          const adjX = nextThisHeadX + dX;
          const adjY = nextThisHeadY + dY;
          if (this.game.isValidPosition(adjX, adjY) && changedPositionsSet.has(checkPosKey(adjX, adjY))) {
            affectedSliders.add(id);
            break;
          }
        }
      }
    }

    return affectedSliders;
  }

  /**
   * 执行一步自动求解
   */
  async step() {
    debugMessage('===== 开始一步求解 =====', DEBUG_MODE);
    
    this.calculateAllMoveValues();

    const bestSliderId = this.getBestSlider();
    if (!bestSliderId) {
      debugMessage('无法找到可移动的滑块，求解结束', DEBUG_MODE);
      return false;
    }

    const slider = this.game.sliders.find(s => s[0] === bestSliderId);
    if (!slider) {
      debugMessage(`滑块 ${bestSliderId} 不存在`, DEBUG_MODE);
      return false;
    }

    debugMessage(`尝试移动滑块 ${bestSliderId}`, DEBUG_MODE);
    const result = this.game.moveSlider(bestSliderId);
    debugMessage(`移动结果: ${result.success}`, DEBUG_MODE);
    
    if (!result.success) {
      return false;
    }

    // 记录移动历史
    if (this.onMoveRecordCallback) {
      this.onMoveRecordCallback(bestSliderId);
    }

    // Handle eliminations first to update game state
    if (this.onMoveCallback) {
      this.onMoveCallback(result.eliminatedSliders);
    }

    // 重新计算所有滑块的运动价值，而不是只更新受影响的滑块
    // 这样可以确保算法的准确性，避免因状态同步问题导致的错误
    this.calculateAllMoveValues();

    debugMessage('===== 一步求解完成 =====', DEBUG_MODE);
    return true;
  }

  /**
   * 停止自动求解
   */
  stop() {
    debugMessage('收到停止信号', DEBUG_MODE);
    this.shouldStop = true;
  }

  /**
   * 自动求解（带延时）
   */
  async solve(delay = 505) {
    debugMessage('===== 开始自动求解 =====', DEBUG_MODE);
    this.shouldStop = false;
    
    let stepCount = 0;
    while (true) {
      if (this.shouldStop) {
        debugMessage('收到停止信号，终止求解', DEBUG_MODE);
        break;
      }

      const gameState = this.game.getGameState();
      debugMessage(`步数: ${stepCount}, 剩余滑块: ${gameState.remainingSliders}, 游戏结束: ${gameState.gameOver}`, DEBUG_MODE);
      
      if (gameState.gameOver) {
        debugMessage('游戏结束，求解完成', DEBUG_MODE);
        break;
      }

      const success = await this.step();
      if (!success) {
        debugMessage('无法继续移动，求解结束', DEBUG_MODE);
        break;
      }

      stepCount++;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    debugMessage(`===== 自动求解结束，总步数: ${stepCount} =====`, DEBUG_MODE);
  }
}
